import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  LocalShipping as ShippingIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";

import { CargoCompany } from "../../../types/cargoCompany";

interface CargoCompanyTableProps {
  companies: CargoCompany[];
  isLoading: boolean;
  error: any;
  onView: (company: CargoCompany) => void;
  onEdit: (company: CargoCompany) => void;
  onDelete: (company: CargoCompany) => void;
  onManageUsers?: (company: CargoCompany) => void;
  onRowClick?: (company: CargoCompany) => void;
}

const CargoCompanyTable: React.FC<CargoCompanyTableProps> = ({
  companies,
  isLoading,
  error,
  onView,
  onEdit,
  onDelete,
  onManageUsers,
  onRowClick,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  const handleRowClick = (company: CargoCompany, event: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons or menu
    const target = event.target as HTMLElement;
    if (
      target.closest("button") ||
      target.closest("[role='button']") ||
      target.closest("svg") ||
      target.closest(".MuiIconButton-root")
    ) {
      return;
    }

    if (onRowClick) {
      onRowClick(company);
    } else if (onManageUsers) {
      onManageUsers(company);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    companyId: string,
  ) => {
    setAnchorEl({ ...anchorEl, [companyId]: event.currentTarget });
  };

  const handleMenuClose = (companyId: string) => {
    setAnchorEl({ ...anchorEl, [companyId]: null });
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {t("admin.cargoCompanies.failedToLoad")}
      </Alert>
    );
  }

  if (isLoading) {
    return <LinearProgress />;
  }

  if (companies.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          {t("admin.cargoCompanies.noCompaniesFound")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("admin.cargoCompanies.name")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.code")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.contactEmail")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.contactPhone")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.orders")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.users")}</TableCell>
            <TableCell>{t("admin.cargoCompanies.status")}</TableCell>
            <TableCell align="right">{t("admin.cargoCompanies.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companies.map((company) => (
            <TableRow
              key={company.id}
              hover
              onClick={(e) => handleRowClick(company, e)}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {company.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={company.code} size="small" variant="outlined" />
              </TableCell>
              <TableCell>{company.contactEmail}</TableCell>
              <TableCell>{company.contactPhone || "-"}</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <ShippingIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {company.totalOrders || 0}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {company.activeUsers || 0}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={company.isActive ? t("admin.cargoCompanies.active") : t("admin.cargoCompanies.inactive")}
                  color={company.isActive ? "success" : "default"}
                  size="small"
                />
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Tooltip title={t("admin.cargoCompanies.viewDetails")}>
                    <IconButton
                      size="small"
                      onClick={() => onView(company)}
                      color="primary"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("admin.cargoCompanies.edit")}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(company)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, company.id)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Menu
                  anchorEl={anchorEl[company.id]}
                  open={Boolean(anchorEl[company.id])}
                  onClose={() => handleMenuClose(company.id)}
                >
                  {onManageUsers && (
                    <MenuItem
                      onClick={() => {
                        onManageUsers(company);
                        handleMenuClose(company.id);
                      }}
                    >
                      <PersonAddIcon fontSize="small" sx={{ mr: 1 }} />
                      {t("admin.cargoCompanies.manageUsers")}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      onDelete(company);
                      handleMenuClose(company.id);
                    }}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    {t("admin.cargoCompanies.delete")}
                  </MenuItem>
                </Menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CargoCompanyTable;

